import React from "react";
import parse, { domToReact, DOMNode, Element } from "html-react-parser";
import { sanitize } from "dompurify";
import styles from "./SanitizedReactHtmlParser.module.css";

// An allowlist rather than additions to DOMPurify's defaults: `ADD_TAGS` and
// `ADD_ATTR` widen what is accepted, which is the opposite of what this
// component is for.
//
// The list covers every tag the translation catalogue uses, plus inline
// formatting siblings that carry no attributes and no URI, so a translator
// adding one of those does not silently lose it. Media and embedding tags are
// deliberately absent: `href` is the only URI-bearing attribute allowed, and
// `ALLOWED_URI_REGEXP` bounds it to the schemes translations actually link to.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "b",
    "strong",
    "i",
    "em",
    "u",
    "span",
    "a",
    "h3",
    "h4",
    "h5",
    "ul",
    "ol",
    "li",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|[/#])/i,
};

const SanitizedReactHtmlParser = ({ content }: { content: string }) => {
  const options = {
    // Only `span` is replaced, to carry the module's own class. Returning
    // anything that is not a React element leaves the node to the parser's
    // default handling, which is what every other allowed tag wants — they
    // render as themselves.
    replace: (domNode: DOMNode) => {
      if ((domNode as Element).name === "span") {
        return (
          <span className={styles.styledParagraph}>
            {domToReact((domNode as Element).children as DOMNode[])}
          </span>
        );
      }
      return domNode;
    },
  };

  return <>{parse(sanitize(content, SANITIZE_CONFIG), options)}</>;
};

export default SanitizedReactHtmlParser;
